/**
 * Board-report-styled Marketing Analytics PDF export.
 * Data-led: ranked tables, clear KPIs, campaign performance, short takeaways.
 */

const CREST =
  'https://www.belvoircastle.com/wp/wp-content/uploads/2026/04/white-peacock.png';

function esc(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function n(val, fallback = 0) {
  const num = Number(val);
  return Number.isFinite(num) ? num : fallback;
}

function formatCurrency(val) {
  if (val === null || val === undefined || val === '') return '—';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);
}

function formatCurrencyExact(val) {
  if (val === null || val === undefined || val === '') return '—';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
  }).format(val);
}

function formatNum(val) {
  return n(val).toLocaleString('en-GB');
}

function formatPct(val, digits = 1) {
  if (val === null || val === undefined || val === '') return '—';
  return `${n(val).toFixed(digits)}%`;
}

function formatDateLong(d = new Date()) {
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatDateShort(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function channelLabel(source, medium, campaign) {
  const src = source || 'direct';
  const med = medium ? ` / ${medium}` : '';
  const camp = campaign ? ` · ${campaign}` : '';
  return `${src}${med}${camp}`;
}

function chartImg(src, alt) {
  if (!src) {
    return `<div class="chart-empty">Chart unavailable for this period</div>`;
  }
  return `<img class="chart-img" src="${src}" alt="${esc(alt)}" />`;
}

function rankBar(value, max, color = '#101d40') {
  const pct = max > 0 ? Math.max(4, Math.round((n(value) / max) * 100)) : 0;
  return `
    <div class="bar-wrap">
      <div class="bar-fill" style="width:${pct}%;background:${color}"></div>
    </div>
  `;
}

function buildStyles() {
  return `
    :root {
      --forest: #101d40;
      --forest-mid: #1a3060;
      --gold: #c9a24b;
      --gold-light: #e0c07a;
      --cream: #f7f3ea;
      --parchment: #ede7d5;
      --charcoal: #2a2a2a;
      --mid-grey: #5a5a5a;
      --light-grey: #9a9a9a;
      --rule: #d4c99a;
      --white: #ffffff;
      --green: #2d7a4f;
      --red: #a94442;
    }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Montserrat', sans-serif;
      background: #dde1e8;
      color: var(--charcoal);
      line-height: 1.55;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    @page { size: A4; margin: 0; }
    @media print {
      html, body { width: 210mm; background: white; margin: 0; padding: 0; }
      .no-print { display: none !important; }
      .page {
        width: 210mm; height: 297mm; min-height: unset; max-height: 297mm;
        margin: 0; padding: 0; break-after: page; page-break-after: always;
        overflow: hidden; position: relative; display: block;
      }
      .page:last-child { break-after: avoid; page-break-after: avoid; }
      .cover { width: 210mm; height: 297mm; min-height: unset; }
    }
    .page {
      width: 210mm; min-height: 297mm; margin: 0 auto 32px;
      background: var(--white); position: relative; overflow: hidden;
    }
    .cover {
      background: var(--forest); display: flex; flex-direction: column;
      align-items: center; justify-content: center; text-align: center;
      padding: 48px 44px; min-height: 297mm;
    }
    .cover-crest { width: 96px; height: 96px; margin-bottom: 22px; }
    .cover-crest img { width: 100%; height: 100%; object-fit: contain; }
    .cover-rule { width: 56px; height: 2px; background: var(--gold); margin: 14px auto; }
    .cover-label {
      font-weight: 600; font-size: 9.5px; letter-spacing: 3.5px;
      text-transform: uppercase; color: var(--gold); margin-bottom: 6px;
    }
    .cover-title {
      font-family: 'Cormorant Garamond', serif; font-weight: 300;
      font-size: 40px; color: var(--white); line-height: 1.15; margin-bottom: 6px;
    }
    .cover-subtitle {
      font-family: 'Cormorant Garamond', serif; font-style: italic;
      font-size: 20px; color: var(--gold-light); margin-bottom: 10px;
    }
    .cover-meta {
      font-size: 9.5px; font-weight: 500; letter-spacing: 1.8px;
      text-transform: uppercase; color: rgba(255,255,255,0.45); margin-top: 8px;
    }
    .cover-bottom {
      position: absolute; bottom: 0; left: 0; right: 0; height: 5px; background: var(--gold);
    }
    .cover-kpi-grid {
      margin-top: 36px; display: grid; grid-template-columns: repeat(3, 1fr);
      gap: 2px; width: 100%; max-width: 480px;
    }
    .cover-kpi {
      background: rgba(255,255,255,0.06); padding: 14px 8px; text-align: center;
    }
    .cover-kpi-val {
      font-family: 'Cormorant Garamond', serif; font-size: 24px;
      color: #e0c07a; font-weight: 600; line-height: 1.1;
    }
    .cover-kpi-label {
      font-size: 7px; letter-spacing: 1.1px; color: rgba(255,255,255,0.42);
      text-transform: uppercase; margin-top: 4px;
    }
    .inner-page { padding: 36px 42px 52px; }
    .page-header {
      display: flex; align-items: center; justify-content: space-between;
      padding-bottom: 10px; border-bottom: 1.5px solid var(--rule); margin-bottom: 18px;
    }
    .page-header-brand {
      font-family: 'Cormorant Garamond', serif; font-size: 11px; font-weight: 500;
      letter-spacing: 2px; text-transform: uppercase; color: var(--forest);
    }
    .page-header-label {
      font-size: 8.5px; font-weight: 600; letter-spacing: 2px;
      text-transform: uppercase; color: var(--light-grey);
    }
    .section-heading {
      font-family: 'Cormorant Garamond', serif; font-size: 22px; font-weight: 500;
      color: var(--forest); margin-bottom: 4px; line-height: 1.2;
    }
    .section-rule {
      width: 32px; height: 2px; background: var(--gold); margin: 8px 0 14px;
    }
    .lede {
      font-size: 10px; color: var(--mid-grey); margin-bottom: 14px; line-height: 1.65;
    }
    .stat-row {
      display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin: 0 0 14px;
    }
    .stat-row.four { grid-template-columns: repeat(4, 1fr); }
    .stat-row.six { grid-template-columns: repeat(3, 1fr); }
    .stat-card {
      background: var(--cream); border-top: 3px solid var(--gold); padding: 12px 12px 10px;
    }
    .stat-value {
      font-family: 'Cormorant Garamond', serif; font-size: 26px; font-weight: 600;
      color: var(--forest); line-height: 1; display: block;
    }
    .stat-label {
      font-size: 7.5px; font-weight: 600; letter-spacing: 1.1px; text-transform: uppercase;
      color: var(--mid-grey); margin-top: 5px; display: block; line-height: 1.35;
    }
    .stat-sub {
      font-size: 8px; color: var(--light-grey); margin-top: 3px; display: block;
    }
    .insight-grid {
      display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin: 0 0 16px;
    }
    .insight-card {
      border: 1px solid var(--parchment); background: var(--white);
      padding: 12px 14px; border-left: 3px solid var(--gold);
    }
    .insight-kicker {
      font-size: 7.5px; font-weight: 700; letter-spacing: 1.3px; text-transform: uppercase;
      color: var(--gold); margin-bottom: 4px;
    }
    .insight-title {
      font-size: 12px; font-weight: 700; color: var(--forest); margin-bottom: 3px;
      line-height: 1.3;
    }
    .insight-meta {
      font-size: 9px; color: var(--mid-grey); line-height: 1.45;
    }
    .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-top: 6px; }
    .chart-block {
      background: var(--cream); border: 1px solid var(--parchment); padding: 10px 12px 8px;
    }
    .chart-block h3, .block-title {
      font-family: 'Cormorant Garamond', serif; font-size: 14px; font-weight: 500;
      color: var(--forest); margin-bottom: 8px;
    }
    .chart-img { width: 100%; height: auto; display: block; max-height: 170px; object-fit: contain; }
    .chart-empty {
      font-size: 9px; color: var(--light-grey); padding: 28px 10px; text-align: center;
    }
    table.data {
      width: 100%; border-collapse: collapse; font-size: 8.5px; margin-top: 4px;
    }
    table.data.compact { font-size: 8px; }
    table.data th {
      text-align: left; padding: 6px 5px; border-bottom: 1.5px solid var(--rule);
      font-size: 7.5px; font-weight: 700; letter-spacing: 0.8px; text-transform: uppercase;
      color: var(--forest); white-space: nowrap;
    }
    table.data td {
      padding: 5px; border-bottom: 1px solid #f0ece2; color: var(--mid-grey); vertical-align: middle;
    }
    table.data td.num { text-align: right; font-weight: 600; color: var(--forest); }
    table.data td.pos { color: var(--green); font-weight: 700; text-align: right; }
    table.data td.neg { color: var(--red); font-weight: 700; text-align: right; }
    table.data tr.top td { background: rgba(201,162,75,0.08); }
    table.data tr:last-child td { border-bottom: none; }
    .rank {
      display: inline-block; min-width: 16px; font-weight: 700; color: var(--forest);
    }
    .bar-wrap {
      height: 5px; background: #efe9da; border-radius: 3px; overflow: hidden; min-width: 48px;
    }
    .bar-fill { height: 100%; border-radius: 3px; }
    .mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 7.5px; }
    .muted { color: var(--light-grey); }
    .badge {
      display: inline-block; padding: 1px 6px; border-radius: 10px;
      font-size: 7px; font-weight: 700; letter-spacing: 0.6px; text-transform: uppercase;
      background: rgba(16,29,64,0.08); color: var(--forest);
    }
    .badge.gold { background: rgba(201,162,75,0.18); color: #8a6a22; }
    .badge.green { background: rgba(45,122,79,0.12); color: var(--green); }
    .split-note {
      margin-top: 10px; padding: 10px 12px; background: var(--forest); color: rgba(255,255,255,0.78);
      font-size: 9px; line-height: 1.55;
    }
    .split-note strong { color: var(--gold-light); }
    .page-footer {
      position: absolute; bottom: 22px; left: 42px; right: 42px;
      display: flex; justify-content: space-between; align-items: center;
      border-top: 1px solid var(--rule); padding-top: 8px;
      font-size: 7.5px; letter-spacing: 1px; text-transform: uppercase; color: var(--light-grey);
    }
    .print-bar {
      position: fixed; bottom: 32px; right: 32px; z-index: 999;
      display: flex; flex-direction: column; align-items: flex-end; gap: 8px;
    }
    .print-btn {
      background: #101d40; color: #c9a24b; border: none; padding: 14px 24px;
      font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 600;
      letter-spacing: 1.5px; text-transform: uppercase; cursor: pointer;
    }
    .print-hint {
      font-size: 10px; color: #5a5a5a; background: white; padding: 8px 12px;
      border: 1px solid #d4c99a; max-width: 220px; text-align: right;
    }
  `;
}

function pageHeader(sectionLabel) {
  return `
    <div class="page-header">
      <div class="page-header-brand">Belvoir Castle</div>
      <div class="page-header-label">${esc(sectionLabel)}</div>
    </div>
  `;
}

function pageFooter(pageNum, totalPages) {
  return `
    <div class="page-footer">
      <span>Confidential &nbsp;|&nbsp; Marketing Analytics</span>
      <span>${pageNum} / ${totalPages}</span>
    </div>
  `;
}

function emptyRow(cols, message) {
  return `<tr><td colspan="${cols}" class="muted" style="text-align:center;padding:16px">${esc(message)}</td></tr>`;
}

/**
 * @param {object} opts
 * @param {object} opts.data
 * @param {number} opts.days
 * @param {object} opts.chartImages
 * @param {Array}  opts.bestPerRegion
 * @param {Array}  opts.topCities
 * @param {Array}  opts.locationRows
 */
export function exportMarketingAnalyticsPdf({
  data,
  days,
  chartImages = {},
  bestPerRegion = [],
  topCities = [],
  locationRows = [],
}) {
  if (!data?.kpis) {
    throw new Error('No analytics data to export');
  }

  const k = data.kpis;
  const periodLabel = `Last ${days} days`;
  const generated = formatDateLong();
  const sources = [...(data.sources || [])];
  const firstTouch = [...(data.first_touch_sources || [])];
  const campaigns = [...(data.campaigns || [])]
    .slice()
    .sort((a, b) =>
      n(b.first_touch_conversions) - n(a.first_touch_conversions)
      || n(b.last_touch_conversions) - n(a.last_touch_conversions)
      || n(b.sessions) - n(a.sessions)
    );
  const recent = [...(data.recent_conversions || [])].slice(0, 14);
  const cities = [...topCities].slice(0, 12);
  const best = [...bestPerRegion].slice(0, 12);
  const loc = [...locationRows].slice(0, 16);

  const topSource = sources[0];
  const topCity = cities[0];
  const topFirstTouch = firstTouch[0];
  const topCampaign = campaigns.find(c =>
    n(c.first_touch_conversions) > 0 || n(c.last_touch_conversions) > 0 || n(c.sessions) > 0
  ) || campaigns[0];
  const topRegion = best[0];

  const maxSourceSessions = Math.max(...sources.map(s => n(s.sessions)), 1);
  const maxCitySessions = Math.max(...cities.map(c => n(c.sessions)), 1);
  const maxCampConv = Math.max(...campaigns.map(c => n(c.first_touch_conversions)), 1);

  const totalPages = 7;

  // ── Ranked source table ────────────────────────────────────────────────────
  const sourceRows = sources.slice(0, 10).map((s, i) => `
    <tr class="${i === 0 ? 'top' : ''}">
      <td><span class="rank">${i + 1}</span></td>
      <td>${esc(s.source || 'direct')}${s.utm_medium ? ` <span class="muted">/ ${esc(s.utm_medium)}</span>` : ''}</td>
      <td>${rankBar(s.sessions, maxSourceSessions)}</td>
      <td class="num">${formatNum(s.sessions)}</td>
      <td class="num">${formatNum(s.visitors ?? s.unique_visitors)}</td>
      <td class="num">${formatNum(s.conversions)}</td>
      <td class="num">${formatCurrency(s.revenue)}</td>
    </tr>
  `).join('') || emptyRow(7, 'No traffic source data for this period.');

  const firstTouchRows = firstTouch.slice(0, 8).map((s, i) => `
    <tr class="${i === 0 ? 'top' : ''}">
      <td><span class="rank">${i + 1}</span></td>
      <td>${esc(s.first_touch_source || 'direct')}${s.first_touch_medium ? ` <span class="muted">/ ${esc(s.first_touch_medium)}</span>` : ''}</td>
      <td class="num">${formatNum(s.conversions)}</td>
      <td class="num">${formatCurrency(s.revenue)}</td>
    </tr>
  `).join('') || emptyRow(4, 'No first-touch conversion data yet.');

  const cityRows = cities.map((c, i) => `
    <tr class="${i === 0 ? 'top' : ''}">
      <td><span class="rank">${i + 1}</span></td>
      <td><strong>${esc(c.city)}</strong></td>
      <td>${esc(c.region || '—')}</td>
      <td>${esc(c.country || '—')}</td>
      <td>${rankBar(c.sessions, maxCitySessions, i === 0 ? '#c9a24b' : '#101d40')}</td>
      <td class="num">${formatNum(c.sessions)}</td>
      <td class="num">${formatNum(c.visitors)}</td>
    </tr>
  `).join('') || emptyRow(7, 'No location data for this period.');

  const bestRows = best.map((r, i) => `
    <tr class="${i === 0 ? 'top' : ''}">
      <td><strong>${esc(r.region)}</strong></td>
      <td>${esc(r.source || '—')}</td>
      <td>${esc(r.medium || '—')}</td>
      <td>${esc(r.campaign || '—')}</td>
      <td class="num">${formatNum(r.sessions)}</td>
      <td class="num">${formatNum(r.conversions)}</td>
      <td class="num">${formatPct(r.conversion_rate)}</td>
      <td class="num">${formatCurrency(r.revenue)}</td>
    </tr>
  `).join('') || emptyRow(8, 'Insufficient regional conversion data.');

  const locRows = loc.map((r, i) => `
    <tr class="${i < 3 ? 'top' : ''}">
      <td>${esc(r.region)}</td>
      <td><strong>${esc(r.city)}</strong></td>
      <td>${esc(r.source || '—')}</td>
      <td>${esc(r.medium || '—')}</td>
      <td>${esc(r.campaign || '—')}</td>
      <td class="num">${formatNum(r.sessions)}</td>
      <td class="num">${formatNum(r.conversions)}</td>
      <td class="num">${formatPct(r.conversion_rate)}</td>
      <td class="num">${formatCurrency(r.revenue)}</td>
    </tr>
  `).join('') || emptyRow(9, 'No location × referral rows for this period.');

  const campaignRows = campaigns.slice(0, 12).map((c, i) => `
      <tr class="${i === 0 ? 'top' : ''}">
        <td>
          <strong>${esc(c.name)}</strong>
          <div class="mono muted">${esc(c.campaign_id || c.utm_campaign)}</div>
        </td>
        <td class="num">${formatNum(c.sessions)}</td>
        <td class="num">${formatNum(c.visitors)}</td>
        <td class="num">${formatNum(c.last_touch_conversions)}</td>
        <td class="num">${formatCurrency(c.last_touch_revenue)}</td>
        <td>${rankBar(c.first_touch_conversions, maxCampConv, '#9d6d49')}</td>
        <td class="num">${formatNum(c.first_touch_conversions)}</td>
        <td class="num">${formatCurrency(c.first_touch_revenue)}</td>
        <td class="num">${formatNum(c.delayed_conversions)}</td>
        <td class="num">${formatCurrency(c.delayed_revenue)}</td>
      </tr>
    `).join('') || emptyRow(10, 'No campaign traffic in this period.');

  const recentRows = recent.map((c) => {
    const first = channelLabel(c.first_source, c.first_medium, c.first_campaign);
    const last = channelLabel(c.last_source, c.last_medium, c.last_campaign);
    const same = (c.first_source || 'direct') === (c.last_source || 'direct')
      && (c.first_medium || '') === (c.last_medium || '');
    return `
      <tr>
        <td class="mono">${esc(c.booking_reference)}</td>
        <td><span class="badge ${c.type === 'membership_signup' ? 'gold' : ''}">${c.type === 'membership_signup' ? 'Membership' : 'Ticket'}</span></td>
        <td class="num">${c.value != null ? formatCurrencyExact(c.value) : '—'}</td>
        <td>${esc(c.city || '—')}${c.region && c.region !== c.city ? `<div class="muted">${esc(c.region)}</div>` : ''}</td>
        <td>${esc(first)}</td>
        <td>${same ? '<span class="muted">same as first</span>' : esc(last)}</td>
        <td class="num">${c.pages_in_session ?? '—'}</td>
        <td class="num">${c.sessions_total ?? '—'}</td>
        <td>${c.is_returning == 1 ? '<span class="badge green">Return</span>' : '<span class="badge">New</span>'}</td>
        <td>${formatDateShort(c.converted_at)}</td>
      </tr>
    `;
  }).join('') || emptyRow(10, 'No conversions recorded in this period.');

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Belvoir Castle — Marketing Analytics Report</title>
  <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Montserrat:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
  <style>${buildStyles()}</style>
</head>
<body>

<div class="no-print print-bar">
  <div class="print-hint">Choose “Save as PDF” in the print dialog for a board-ready document.</div>
  <button class="print-btn" onclick="window.print()">Print / Save PDF</button>
</div>

<!-- COVER -->
<div class="page">
  <div class="cover">
    <div class="cover-crest">
      <img src="${CREST}" alt="Belvoir Castle Peacock" />
    </div>
    <div class="cover-label">Marketing Intelligence Briefing</div>
    <div class="cover-rule"></div>
    <div class="cover-title">Belvoir Castle</div>
    <div class="cover-subtitle">Marketing Analytics Report</div>
    <div class="cover-rule"></div>
    <div class="cover-label" style="color:rgba(255,255,255,0.5);letter-spacing:2px;margin-top:4px;">
      ${esc(periodLabel)} &nbsp;·&nbsp; First-party data
    </div>
    <div class="cover-kpi-grid">
      <div class="cover-kpi">
        <div class="cover-kpi-val">${formatNum(k.unique_visitors)}</div>
        <div class="cover-kpi-label">Unique Visitors</div>
      </div>
      <div class="cover-kpi">
        <div class="cover-kpi-val">${formatNum(k.total_sessions)}</div>
        <div class="cover-kpi-label">Sessions</div>
      </div>
      <div class="cover-kpi">
        <div class="cover-kpi-val">${formatNum(k.total_conversions)}</div>
        <div class="cover-kpi-label">Conversions</div>
      </div>
      <div class="cover-kpi">
        <div class="cover-kpi-val">${formatCurrency(k.total_revenue)}</div>
        <div class="cover-kpi-label">Attributed Revenue</div>
      </div>
      <div class="cover-kpi">
        <div class="cover-kpi-val">${formatPct(k.conversion_rate, 2)}</div>
        <div class="cover-kpi-label">Conversion Rate</div>
      </div>
      <div class="cover-kpi">
        <div class="cover-kpi-val">${formatPct(k.returning_conversion_rate)}</div>
        <div class="cover-kpi-label">Returning Buyers</div>
      </div>
    </div>
    <div class="cover-meta" style="margin-top:40px;">
      Confidential &nbsp;|&nbsp; Generated ${esc(generated)}
    </div>
    <div class="cover-bottom"></div>
  </div>
</div>

<!-- PAGE 2 — Snapshot -->
<div class="page">
  <div class="inner-page">
    ${pageHeader('Executive Snapshot')}
    <div class="section-heading">What the numbers say</div>
    <div class="section-rule"></div>
    <p class="lede">
      Ranked highlights from first-party website analytics for ${esc(periodLabel).toLowerCase()}.
      Attribution is last-touch on the booking session unless marked first-touch.
    </p>

    <div class="stat-row four">
      <div class="stat-card">
        <span class="stat-value">${formatNum(k.unique_visitors)}</span>
        <span class="stat-label">Unique Visitors</span>
        <span class="stat-sub">${formatNum(k.total_sessions)} sessions · ${formatPct(k.returning_rate)} return</span>
      </div>
      <div class="stat-card">
        <span class="stat-value">${formatNum(k.total_conversions)}</span>
        <span class="stat-label">Total Conversions</span>
        <span class="stat-sub">${formatNum(k.ticket_bookings)} tickets · ${formatNum(k.membership_signups)} memberships</span>
      </div>
      <div class="stat-card">
        <span class="stat-value">${formatCurrency(k.total_revenue)}</span>
        <span class="stat-label">Attributed Revenue</span>
        <span class="stat-sub">${formatPct(k.conversion_rate, 2)} of sessions convert</span>
      </div>
      <div class="stat-card">
        <span class="stat-value">${k.avg_sessions_to_convert ?? 0}</span>
        <span class="stat-label">Avg Sessions to Buy</span>
        <span class="stat-sub">${k.avg_pages_before_conversion ?? 0} pages in booking session</span>
      </div>
    </div>

    <div class="insight-grid">
      <div class="insight-card">
        <div class="insight-kicker">Top traffic source</div>
        <div class="insight-title">${esc(topSource ? channelLabel(topSource.source, topSource.utm_medium) : 'No data yet')}</div>
        <div class="insight-meta">
          ${topSource
            ? `${formatNum(topSource.sessions)} sessions · ${formatNum(topSource.conversions)} conversions · ${formatCurrency(topSource.revenue)} revenue`
            : 'Sources will appear once UTM / referral data is collected.'}
        </div>
      </div>
      <div class="insight-card">
        <div class="insight-kicker">Top first-touch (buyers)</div>
        <div class="insight-title">${esc(topFirstTouch ? channelLabel(topFirstTouch.first_touch_source, topFirstTouch.first_touch_medium) : 'No data yet')}</div>
        <div class="insight-meta">
          ${topFirstTouch
            ? `${formatNum(topFirstTouch.conversions)} buyers originally arrived via this channel · ${formatCurrency(topFirstTouch.revenue)}`
            : 'Shows the original acquisition source for people who later bought.'}
        </div>
      </div>
      <div class="insight-card">
        <div class="insight-kicker">Top town / city</div>
        <div class="insight-title">${esc(topCity ? `${topCity.city}${topCity.region ? `, ${topCity.region}` : ''}` : 'No data yet')}</div>
        <div class="insight-meta">
          ${topCity
            ? `${formatNum(topCity.sessions)} sessions · ${formatNum(topCity.visitors)} visitors`
            : 'Approximate location from IP at session start.'}
        </div>
      </div>
      <div class="insight-card">
        <div class="insight-kicker">Best region × channel</div>
        <div class="insight-title">${esc(topRegion ? `${topRegion.region} · ${channelLabel(topRegion.source, topRegion.medium, topRegion.campaign)}` : 'No data yet')}</div>
        <div class="insight-meta">
          ${topRegion
            ? `${formatNum(topRegion.conversions)} conversions · ${formatPct(topRegion.conversion_rate)} rate · ${formatCurrency(topRegion.revenue)}`
            : 'Needs both geo and UTM / referral data on converting sessions.'}
        </div>
      </div>
      <div class="insight-card">
        <div class="insight-kicker">Strongest campaign (first-touch)</div>
        <div class="insight-title">${esc(topCampaign?.name || 'No campaign traffic yet')}</div>
        <div class="insight-meta">
          ${topCampaign
            ? `${formatNum(topCampaign.sessions)} sessions · ${formatNum(topCampaign.first_touch_conversions)} first-touch purchases · ${formatNum(topCampaign.delayed_conversions)} returned & bought · ${formatCurrency(topCampaign.first_touch_revenue)}`
            : 'Campaigns appear automatically from utm_campaign on ad landings.'}
        </div>
      </div>
      <div class="insight-card">
        <div class="insight-kicker">Buyer journey</div>
        <div class="insight-title">${formatPct(k.returning_conversion_rate)} of buyers had visited before</div>
        <div class="insight-meta">
          Average ${k.avg_sessions_to_convert ?? 0} visits and ${k.avg_pages_before_conversion ?? 0} pages before purchase.
          Multi-day journeys are captured via persistent visitor ID.
        </div>
      </div>
    </div>

    ${pageFooter(2, totalPages)}
  </div>
</div>

<!-- PAGE 3 — Traffic & attribution -->
<div class="page">
  <div class="inner-page">
    ${pageHeader('Traffic & Attribution')}
    <div class="section-heading">Sources ranked by volume</div>
    <div class="section-rule"></div>

    <div class="two-col" style="margin-bottom:14px;">
      <div class="chart-block">
        <h3>Sessions over time</h3>
        ${chartImg(chartImages.sessions, 'Sessions chart')}
      </div>
      <div class="chart-block">
        <h3>Conversions over time</h3>
        ${chartImg(chartImages.conversions, 'Conversions chart')}
      </div>
    </div>

    <div class="block-title">Last-touch sources (session that booked)</div>
    <table class="data compact">
      <thead>
        <tr>
          <th>#</th><th>Source / Medium</th><th style="width:70px"></th>
          <th style="text-align:right">Sessions</th>
          <th style="text-align:right">Visitors</th>
          <th style="text-align:right">Conv.</th>
          <th style="text-align:right">Revenue</th>
        </tr>
      </thead>
      <tbody>${sourceRows}</tbody>
    </table>

    <div style="height:12px"></div>
    <div class="two-col">
      <div>
        <div class="block-title">First-touch (where buyers originally came from)</div>
        <table class="data compact">
          <thead>
            <tr>
              <th>#</th><th>Source / Medium</th>
              <th style="text-align:right">Buyers</th>
              <th style="text-align:right">Revenue</th>
            </tr>
          </thead>
          <tbody>${firstTouchRows}</tbody>
        </table>
      </div>
      <div class="chart-block">
        <h3>First-touch chart</h3>
        ${chartImg(chartImages.firstTouch, 'First-touch chart')}
      </div>
    </div>

    ${pageFooter(3, totalPages)}
  </div>
</div>

<!-- PAGE 4 — Geography -->
<div class="page">
  <div class="inner-page">
    ${pageHeader('Geographic Reach')}
    <div class="section-heading">Towns &amp; cities ranked by sessions</div>
    <div class="section-rule"></div>
    <p class="lede">
      Approximate IP-based location at session start. Gold-highlighted row is the leading market for this period.
    </p>

    <div class="chart-block" style="margin-bottom:12px;">
      <h3>Top towns &amp; cities</h3>
      ${chartImg(chartImages.cities, 'Cities chart')}
    </div>

    <table class="data">
      <thead>
        <tr>
          <th>#</th><th>Town / City</th><th>Region</th><th>Country</th>
          <th style="width:70px"></th>
          <th style="text-align:right">Sessions</th>
          <th style="text-align:right">Visitors</th>
        </tr>
      </thead>
      <tbody>${cityRows}</tbody>
    </table>
    ${pageFooter(4, totalPages)}
  </div>
</div>

<!-- PAGE 5 — Campaigns -->
<div class="page">
  <div class="inner-page">
    ${pageHeader('Campaign Performance')}
    <div class="section-heading">Automatic campaign attribution</div>
    <div class="section-rule"></div>
    <p class="lede">
      First-party tracking only — no manual Meta entry.
      <strong>Last-touch</strong> = purchased in a session from this campaign.
      <strong>First-touch</strong> = originally arrived via this campaign (including return visits).
      <strong>Returned &amp; bought</strong> = first-touch from campaign, purchase on a later visit.
      Period: ${esc(periodLabel).toLowerCase()}.
    </p>

    <table class="data compact">
      <thead>
        <tr>
          <th>Campaign</th>
          <th style="text-align:right">Sess.</th>
          <th style="text-align:right">Visitors</th>
          <th style="text-align:right">Last-touch</th>
          <th style="text-align:right">LT Rev.</th>
          <th style="width:48px"></th>
          <th style="text-align:right">First-touch</th>
          <th style="text-align:right">FT Rev.</th>
          <th style="text-align:right">Returned</th>
          <th style="text-align:right">Ret. Rev.</th>
        </tr>
      </thead>
      <tbody>${campaignRows}</tbody>
    </table>

    <div class="split-note">
      <strong>How to read this:</strong>
      Ranked by first-touch purchases. Use Meta Ads Manager for spend / CPM / platform ROAS.
      Website figures answer: which campaigns drove traffic, which led to purchases, and which
      brought people who came back later and bought.
    </div>
    ${pageFooter(5, totalPages)}
  </div>
</div>

<!-- PAGE 6 — Regional effectiveness -->
<div class="page">
  <div class="inner-page">
    ${pageHeader('Regional Effectiveness')}
    <div class="section-heading">What’s working where</div>
    <div class="section-rule"></div>

    <div class="block-title">Most effective channel per region</div>
    <table class="data compact" style="margin-bottom:16px;">
      <thead>
        <tr>
          <th>Region</th><th>Source</th><th>Medium</th><th>Campaign</th>
          <th style="text-align:right">Sessions</th>
          <th style="text-align:right">Conv.</th>
          <th style="text-align:right">Rate</th>
          <th style="text-align:right">Revenue</th>
        </tr>
      </thead>
      <tbody>${bestRows}</tbody>
    </table>

    <div class="block-title">Top location × referral combinations</div>
    <table class="data compact">
      <thead>
        <tr>
          <th>Region</th><th>City</th><th>Source</th><th>Medium</th><th>Campaign</th>
          <th style="text-align:right">Sess.</th>
          <th style="text-align:right">Conv.</th>
          <th style="text-align:right">Rate</th>
          <th style="text-align:right">Revenue</th>
        </tr>
      </thead>
      <tbody>${locRows}</tbody>
    </table>
    ${pageFooter(6, totalPages)}
  </div>
</div>

<!-- PAGE 7 — Journeys -->
<div class="page">
  <div class="inner-page">
    ${pageHeader('Conversion Journeys')}
    <div class="section-heading">Recent bookings — path to purchase</div>
    <div class="section-rule"></div>
    <p class="lede">
      <strong>First touch</strong> = original acquisition source.
      <strong>Last touch</strong> = source on the booking session.
      <strong>Visits</strong> = total site sessions before converting (captures return-tomorrow buyers).
    </p>

    <div class="stat-row" style="margin-bottom:12px;">
      <div class="stat-card">
        <span class="stat-value">${formatPct(k.returning_conversion_rate)}</span>
        <span class="stat-label">Returning Buyers</span>
      </div>
      <div class="stat-card">
        <span class="stat-value">${k.avg_sessions_to_convert ?? 0}</span>
        <span class="stat-label">Avg Sessions to Convert</span>
      </div>
      <div class="stat-card">
        <span class="stat-value">${k.avg_pages_before_conversion ?? 0}</span>
        <span class="stat-label">Avg Pages Before Buying</span>
      </div>
    </div>

    <table class="data compact">
      <thead>
        <tr>
          <th>Ref</th><th>Type</th><th style="text-align:right">Value</th>
          <th>Location</th><th>First Touch</th><th>Last Touch</th>
          <th style="text-align:right">Pages</th>
          <th style="text-align:right">Visits</th>
          <th>Buyer</th><th>Date</th>
        </tr>
      </thead>
      <tbody>${recentRows}</tbody>
    </table>

    <div class="split-note" style="margin-top:14px;">
      <strong>Belvoir Castle Estate</strong> — Confidential. First-party analytics from belvoircastle.com.
      Generated ${esc(generated)} for ${esc(periodLabel).toLowerCase()}.
    </div>
    ${pageFooter(7, totalPages)}
  </div>
</div>

</body>
</html>`;

  const win = window.open('', '_blank');
  if (!win) {
    throw new Error('Pop-up blocked — please allow pop-ups to export the PDF');
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
}
